import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { CheckCircle2, Database, Mail, Smartphone, TriangleAlert } from "lucide-react";
import { PushCard } from "@/components/push/PushCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormError, PageHeader } from "@/components/common/ui-bits";
import { api, errMsg } from "@/lib/api";

const when = new Intl.DateTimeFormat("en-US", { timeZone: "America/Nassau", dateStyle: "medium", timeStyle: "short" });
const ITEMS = [
  ["transactions", "Transactions"], ["bills", "Bills"], ["bill_payments", "Bill payments"], ["budgets", "Budgets & overall target"],
  ["alert_log", "Alert email log"], ["notifications", "Notifications"], ["import_batches", "Import history"],
  ["custom_categories", "Custom categories"], ["demo_staff", "Demo staff account"],
];

function ClearDialog({ open, onClose, onDone }) {
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const close = () => { setText(""); setError(""); onClose(); };
  const run = async () => {
    setBusy(true);
    try {
      await api.post("/settings/clear-data", { confirm: text });
      toast.success("Demo & test data cleared");
      setText(""); onDone();
    } catch (e) { setError(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md bg-card rounded-2xl" data-testid="clear-data-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Clear demo & test data?</DialogTitle>
          <DialogDescription>
            This permanently removes all transactions, bills, payments, budgets, the overall target, alert logs, notifications and import history.
            The 9 default categories and all admin accounts stay. The demo staff account staff@dalilshop.bs is removed. This can't be undone.
          </DialogDescription>
        </DialogHeader>
        <div><Label htmlFor="clear-confirm">Type DELETE to confirm</Label>
          <Input id="clear-confirm" value={text} onChange={(e) => { setText(e.target.value); setError(""); }} autoComplete="off" className="mt-1.5 h-11 rounded-xl font-num" data-testid="clear-data-confirm-input" /></div>
        <FormError message={error} testId="clear-data-error" />
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={close} className="rounded-xl" data-testid="clear-data-cancel">Cancel</Button>
          <Button disabled={text !== "DELETE" || busy} onClick={run} className="rounded-xl bg-rose-600 hover:bg-rose-700" data-testid="clear-data-confirm-button">
            {busy ? "Clearing…" : "Clear data"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EmailCard({ email, url }) {
  return (
    <section className="surface p-5 sm:p-6 mb-6 fade-up" data-testid="email-delivery-card">
      <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><Mail className="h-5 w-5" />Email delivery</h2>
      {email.test_mode ? (
        <p className="mt-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 flex gap-2" data-testid="email-mode-test">
          <TriangleAlert className="h-4 w-4 mt-0.5 shrink-0" />Test mode is on. Every alert email goes to {email.redirect_to}.</p>
      ) : (
        <p className="mt-2 text-sm text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 flex gap-2" data-testid="email-mode-live">
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />Budget alerts and due-date reminders go to every active admin's own inbox.</p>
      )}
      <p className="mt-2 text-xs text-muted-foreground" data-testid="email-public-url">Links in emails open {url || "this app's address (detected on next sign-in)"}</p>
    </section>
  );
}

export default function SettingsPage() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/settings/data").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const r = data.last_reset;
  return (
    <div className="max-w-3xl">
      <PageHeader eyebrow="Admin" title="Settings" testId="settings-title" />
      <Link to="/install" className="surface mb-6 px-5 py-4 flex items-center gap-3 font-semibold text-sm hover:bg-secondary/40 fade-up" data-testid="settings-install-link"><Smartphone className="h-5 w-5 text-sky-700" />Install the app</Link>
      <PushCard />
      <EmailCard email={data.email} url={data.public_url} />
      <section className="surface p-5 sm:p-6 fade-up" data-testid="settings-data-section">
        <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><Database className="h-5 w-5" />Data</h2>
        <p className="text-sm text-muted-foreground mt-1">Start fresh with your real figures. Remove the demo and test records before going live.</p>
        <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm" data-testid="settings-data-counts">
          {ITEMS.map(([k, label]) => (
            <li key={k} className="flex justify-between border-b border-border/60 py-1"><span>{label}</span>
              <b className="font-num" data-testid={`settings-count-${k}`}>{data.counts[k]}</b></li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground mt-3">Kept: the 9 default categories and every admin account.</p>
        <Button onClick={() => setOpen(true)} className="mt-4 rounded-xl h-11 bg-rose-600 hover:bg-rose-700" data-testid="clear-data-button">Clear demo & test data</Button>
        {r && <p className="mt-3 text-xs text-muted-foreground" data-testid="settings-last-reset">Last cleared by {r.by_name} ({r.by_email}) on {when.format(new Date(r.at))}</p>}
      </section>
      <ClearDialog open={open} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />
    </div>
  );
}
